import { z } from 'zod';
import { BlockSchema, OptionSchema } from './blocks';
import { UuidSchema } from './ids';
import { ConditionNodeSchema, LogicRuleSchema } from './logic';
import { FormSettingsSchema } from './definition';
import { TextContentSchema } from './richText';

export const LIFECYCLE_COMMAND_TYPES = ['publish_form', 'close_form', 'archive_form'] as const;
export const MUTATING_COMMAND_TYPES = new Set([
  'add_block', 'update_block', 'move_block', 'delete_block', 'add_option',
  'update_option', 'move_option', 'delete_option', 'set_logic_rule',
  'delete_logic_rule', 'update_form_settings', 'update_form_title',
]);

const AddBlock = z.object({ type: z.literal('add_block'), after_block_id: UuidSchema.nullable().default(null), block: BlockSchema }).strict();
const UpdateBlock = z.object({ type: z.literal('update_block'), block: BlockSchema }).strict();
const MoveBlock = z.object({ type: z.literal('move_block'), block_id: UuidSchema, after_block_id: UuidSchema.nullable().default(null) }).strict();
const DeleteBlock = z.object({ type: z.literal('delete_block'), block_id: UuidSchema }).strict();
const AddOption = z.object({ type: z.literal('add_option'), block_id: UuidSchema, after_option_id: UuidSchema.nullable().default(null), option: OptionSchema }).strict();
const UpdateOption = z.object({ type: z.literal('update_option'), block_id: UuidSchema, option: OptionSchema }).strict();
const MoveOption = z.object({ type: z.literal('move_option'), block_id: UuidSchema, option_id: UuidSchema, after_option_id: UuidSchema.nullable().default(null) }).strict();
const DeleteOption = z.object({ type: z.literal('delete_option'), block_id: UuidSchema, option_id: UuidSchema }).strict();
const SetLogicRule = z.object({ type: z.literal('set_logic_rule'), rule: LogicRuleSchema, condition_nodes: z.array(ConditionNodeSchema).default([]) }).strict();
const DeleteLogicRule = z.object({ type: z.literal('delete_logic_rule'), logic_rule_id: UuidSchema }).strict();
const UpdateFormSettings = z.object({ type: z.literal('update_form_settings'), settings: FormSettingsSchema }).strict();
const UpdateFormTitle = z.object({ type: z.literal('update_form_title'), title: TextContentSchema }).strict();
const PublishForm = z.object({ type: z.literal('publish_form') }).strict();
const CloseForm = z.object({ type: z.literal('close_form') }).strict();
const ArchiveForm = z.object({ type: z.literal('archive_form') }).strict();

export const CommandSchema = z.discriminatedUnion('type', [
  AddBlock, UpdateBlock, MoveBlock, DeleteBlock,
  AddOption, UpdateOption, MoveOption, DeleteOption,
  SetLogicRule, DeleteLogicRule, UpdateFormSettings, UpdateFormTitle,
  PublishForm, CloseForm, ArchiveForm,
]);
export type Command = z.infer<typeof CommandSchema>;

export const CommandBatchSchema = z
  .object({
    schema_version: z.literal(1).default(1),
    form_id: UuidSchema,
    base_draft_version: z.number().int().nonnegative(),
    operation_id: UuidSchema,
    commands: z.array(CommandSchema).min(1),
  })
  .strict();
export type CommandBatch = z.infer<typeof CommandBatchSchema>;
